import { Avatar, type AvatarProps } from '@mui/material'
import type React from 'react'
import { useEffect, useState } from 'react'

interface ProfileAvatarProps extends Omit<AvatarProps, 'src'> {
  src?: string
  alt?: string
  fallback?: React.ReactNode
}

const CACHE_KEY = 'profile_image_cache'
const CACHE_EXPIRY_KEY = 'profile_image_cache_expiry'
const CACHE_DURATION = 24 * 60 * 60 * 1000 // 24 hours

const getImageCache = (): Map<string, string> => {
  try {
    const expiryTime = localStorage.getItem(CACHE_EXPIRY_KEY)
    if (expiryTime && Date.now() > Number.parseInt(expiryTime)) {
      // Cache expired, clear it
      localStorage.removeItem(CACHE_KEY)
      localStorage.removeItem(CACHE_EXPIRY_KEY)
      return new Map()
    }

    const cached = localStorage.getItem(CACHE_KEY)
    return cached ? new Map(JSON.parse(cached)) : new Map()
  } catch {
    return new Map()
  }
}

const setImageCache = (cache: Map<string, string>) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(Array.from(cache.entries())))
    localStorage.setItem(CACHE_EXPIRY_KEY, (Date.now() + CACHE_DURATION).toString())
  } catch {
    // Storage full or disabled, ignore
  }
}

export const ProfileAvatar: React.FC<ProfileAvatarProps> = ({
  src,
  alt,
  fallback,
  ...avatarProps
}) => {
  const [imageSrc, setImageSrc] = useState<string | undefined>()
  const [hasError, setHasError] = useState(false)

  useEffect(() => {
    if (!src) {
      setImageSrc(undefined)
      setHasError(false)
      return
    }

    // Check localStorage cache first
    const cache = getImageCache()
    if (cache.has(src)) {
      setImageSrc(cache.get(src))
      setHasError(false)
      return
    }

    // Try different Google image URL patterns to avoid rate limits
    let optimizedSrc = src
    if (src.includes('googleusercontent.com')) {
      // Try removing all size parameters first (uses default size)
      optimizedSrc = src.replace(/=s\d+-c$/, '')
      // If that doesn't work, we'll fall back to a different size
      if (optimizedSrc === src) {
        optimizedSrc = src.replace(/=s\d+-c$/, '=s96-c') // Use larger size
      }
    }

    // Add delay to avoid rapid requests
    const loadImage = () => {
      const img = new Image()
      img.onload = () => {
        // Save to localStorage cache
        const cache = getImageCache()
        cache.set(src, optimizedSrc)
        setImageCache(cache)

        setImageSrc(optimizedSrc)
        setHasError(false)
      }
      img.onerror = () => {
        console.warn('Profile image failed to load:', optimizedSrc)
        setHasError(true)
        setImageSrc(undefined)
      }
      img.src = optimizedSrc
    }

    // Add small delay to prevent rapid-fire requests during development
    const timeoutId = setTimeout(loadImage, 100)
    return () => clearTimeout(timeoutId)
  }, [src])

  if (hasError || !imageSrc) {
    return (
      <Avatar {...avatarProps} alt={alt}>
        {fallback}
      </Avatar>
    )
  }

  return (
    <Avatar
      {...avatarProps}
      src={imageSrc}
      alt={alt}
      onError={() => {
        setHasError(true)
        setImageSrc(undefined)
      }}
    >
      {fallback}
    </Avatar>
  )
}
